import type {
    Breadcrumb,
    ChildContainerProps,
    LayoutConfig,
    LayoutContextProps,
    LayoutState,
    ColorScheme,
} from "@/types";
import React, { useState, useEffect } from "react";

export const LayoutContext = React.createContext({} as LayoutContextProps);

export const LayoutProvider = (props: ChildContainerProps) => {

    const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
    const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>({
        ripple: false,
        inputStyle: "filled",
        menuMode: "static",
        menuTheme: "colorScheme",
        colorScheme: "light",
        theme: "indigo",
        scale: 14,
    });

    const isDark = layoutConfig.colorScheme === "dark" || layoutConfig.colorScheme === "dim";

    const colors = {
        primaryColor: isDark ? '#dadadaff' : '#0369a1',
        primaryBg: isDark ? '#083344' : '#e0f2fe',
        primaryText: isDark ? '#38bdf8' : '#0284c7',
        navbarBg: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        footerBg: isDark ? '#0f172a' : '#f8fafc',
        textColor: isDark ? '#f1f5f9' : '#1e293b',
        lightText: isDark ? '#94a3b8' : '#64748b',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        cardBg: isDark ? '#1e293b' : '#ffffff',
        surfaceBg: isDark ? '#0f172a' : '#f1f5f9',
        primaryLight: isDark ? '#38bdf8' : '#0284c7',
        successColor: isDark ? '#10b981' : '#059669',
        warningColor: isDark ? '#f59e0b' : '#d97706',
        dangerColor: isDark ? '#ef4444' : '#dc2626',
        infoColor: isDark ? '#3b82f6' : '#2563eb',
        hoverBg: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        
    }


    const [layoutState, setLayoutState] = useState<LayoutState>({
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        overlaySubmenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        resetMenu: false,
        sidebarActive: false,
        anchored: false,
    });


    const onMenuToggle = () => {
        if (isOverlay()) {
            setLayoutState((prevLayoutState:LayoutState) => ({
                ...prevLayoutState,
                overlayMenuActive: !prevLayoutState.overlayMenuActive,
            }));
        }

        if (isDesktop()) {
            setLayoutState((prevLayoutState:LayoutState) => ({
                ...prevLayoutState,
                staticMenuDesktopInactive:
                    !prevLayoutState.staticMenuDesktopInactive,
            }));
        } else {
            setLayoutState((prevLayoutState:LayoutState) => ({
                ...prevLayoutState,
                staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive,
            }));
        }
    };

    const showConfigSidebar = () => {
        setLayoutState((prevLayoutState:LayoutState) => ({
            ...prevLayoutState,
            configSidebarVisible: true,
        }));
    };

    const showProfileSidebar = () => {
        setLayoutState((prevLayoutState:LayoutState) => ({
            ...prevLayoutState,
            profileSidebarVisible: !prevLayoutState.profileSidebarVisible,
        }));
    };

    const isOverlay = () => {
        return layoutConfig.menuMode === "overlay";
    };

    const isSlim = () => {
        return layoutConfig.menuMode === "slim";
    };

    const isSlimPlus = () => {
        return layoutConfig.menuMode === "slim-plus";
    };

    const isHorizontal = () => {
        return layoutConfig.menuMode === "horizontal";
    };

    const isDesktop = () => {
        return window.innerWidth > 991;
    };

    const value = {
        layoutConfig,
        setLayoutConfig,
        layoutState,
        setLayoutState,
        onMenuToggle,
        showConfigSidebar,
        showProfileSidebar,
        isSlim,
        isSlimPlus,
        isHorizontal,
        isDesktop,
        breadcrumbs,
        setBreadcrumbs,
        colors,
    };

    return (
        <LayoutContext.Provider value={value}>
            <>
              {props.children}
            </>
        </LayoutContext.Provider>
    );
};
